import React, { useState, useCallback } from 'react';

/**
 * SkillMeter Component
 * 
 * Displays a visual representation of skill proficiency with expandable details.
 * Used in the Competency Assessment module.
 * 
 * @param {string} name - Skill name
 * @param {number} level - Skill level (0-100)
 * @param {string} description - Skill description
 * @param {Array} examples - Examples of skill application
 */
const SkillMeter = ({ name, level, description, examples = [] }) => {
  const [expanded, setExpanded] = useState(false);
  
  /**
   * Toggle the expanded state of the skill details
   */
  const toggleExpanded = useCallback(() => {
    setExpanded(prevExpanded => !prevExpanded);
  }, []);
  
  /**
   * Determine color class based on skill level
   * @returns {string} Tailwind CSS class for background color
   */
  const getColorClass = useCallback(() => {
    if (level >= 90) return 'bg-green-500';
    if (level >= 80) return 'bg-blue-500';
    if (level >= 70) return 'bg-yellow-500';
    return 'bg-orange-500';
  }, [level]);
  
  /**
   * Determine text color class based on skill level
   * @returns {string} Tailwind CSS class for text color
   */
  const getTextColorClass = useCallback(() => {
    if (level >= 90) return 'text-green-600';
    if (level >= 80) return 'text-blue-600';
    if (level >= 70) return 'text-yellow-600';
    return 'text-orange-600';
  }, [level]);
  
  return (
    <div 
      className="border rounded-lg cursor-pointer hover:border-blue-300 transition-colors"
      onClick={toggleExpanded}
      role="button"
      aria-expanded={expanded}
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          toggleExpanded();
        }
      }}
    >
      <div className="p-3 flex justify-between items-center">
        <span className="font-medium">{name}</span>
        <div className="flex items-center gap-2">
          <span className={`text-sm ${getTextColorClass()}`}>
            {level}%
          </span>
          <span aria-hidden="true">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>
      
      <div className="skill-meter" aria-label={`Skill level: ${level}%`}>
        <div 
          className={`h-2 ${getColorClass()}`} 
          style={{ width: `${level}%` }}
        ></div>
      </div>
      
      {expanded && (
        <div className="p-3 border-t">
          <p className="text-slate-600">{description}</p>
          
          {examples && examples.length > 0 && (
            <>
              <h4 className="font-medium mt-2 mb-1">Examples:</h4>
              <ul className="text-sm space-y-1">
                {examples.map((example, index) => (
                  <li key={index} className="flex items-start gap-1">
                    <span className="text-blue-500" aria-hidden="true">→</span>
                    <span>{example}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SkillMeter;
